import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  ChevronDown,
  Download,
  Edit3,
  FileClock,
  History,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Upload,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ExportFormatMenuItems } from "@/components/catalog/export-format-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { saveDownload } from "@/lib/download";
import {
  exportErrorMessage,
  exportSuccessMessage,
  type ExportFormat,
} from "@/lib/export-format";
import {
  ModerationRulesetService,
  type ModerationReadiness,
  type ModerationReferenceImage,
  type ModerationRule,
  type ModerationRuleset,
  type ModerationSeverity,
} from "@/services/moderation-ruleset.service";
import { RuleEditorDialog } from "./rule-editor-dialog";
import {
  AssignmentsDialog,
  ImportRulesetDialog,
  RulesetDetailsDialog,
  VersionHistoryDialog,
} from "./ruleset-dialogs";

type RuleDialogState = { mode: "create" } | { mode: "edit"; rule: ModerationRule } | null;
type SeverityFilter = "all" | ModerationSeverity;
type ReadinessFilter = "all" | ModerationReadiness;
type ActivityFilter = "all" | "active" | "inactive";
type SuccessNotice =
  | { kind: "text"; text: string }
  | { kind: "rule"; ruleId: string; ruleName: string; version: number };

const readinessLabels: Record<ModerationReadiness, string> = {
  preparing: "Preparing",
  ready: "Ready",
  needs_attention: "Needs attention",
};

function runAtLabel(rule: ModerationRule): string {
  if (rule.runAt === "before_enrichment") return "Before enrichment";
  if (rule.runAt === "after_enrichment") return "After enrichment";
  return "Before and after";
}

function imageScopeLabel(rule: ModerationRule): string {
  return rule.imageScope === "all_images" ? "All images (up to 6)" : "First image";
}

function severityClass(severity: ModerationSeverity): string {
  if (severity === "high") return "border-red-200 bg-red-50 text-red-700";
  if (severity === "medium") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-sky-200 bg-sky-50 text-sky-700";
}

function readinessClass(readiness: ModerationReadiness): string {
  if (readiness === "ready") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (readiness === "preparing") return "border-sky-200 bg-sky-50 text-sky-700";
  return "border-red-200 bg-red-50 text-red-700";
}

export function ModerationRulesetManagement() {
  const [searchParams] = useSearchParams();
  const linkedRulesetId = searchParams.get("ruleset");
  const [rulesets, setRulesets] = useState<ModerationRuleset[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(linkedRulesetId);
  const [detail, setDetail] = useState<ModerationRuleset | null>(null);
  const [rules, setRules] = useState<ModerationRule[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<SuccessNotice | null>(null);
  const [rulesetQuery, setRulesetQuery] = useState("");
  const [ruleQuery, setRuleQuery] = useState("");
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  const [readinessFilter, setReadinessFilter] = useState<ReadinessFilter>("all");
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("all");
  const [rulesetDialog, setRulesetDialog] = useState<"create" | "edit" | null>(null);
  const [ruleDialog, setRuleDialog] = useState<RuleDialogState>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [assignmentsOpen, setAssignmentsOpen] = useState(false);
  const [historyRule, setHistoryRule] = useState<ModerationRule | null>(null);
  const [busyRuleId, setBusyRuleId] = useState<string | null>(null);
  const [rulesetBusy, setRulesetBusy] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);

  const loadRulesets = useCallback(async (preferredId?: string | null) => {
    const items = await ModerationRulesetService.listRulesets();
    setRulesets(items);
    setSelectedId((current) => {
      const preferred = preferredId ?? current;
      return preferred && items.some((item) => item.id === preferred)
        ? preferred
        : items.find((item) => !item.archivedAt)?.id ?? items[0]?.id ?? null;
    });
    return items;
  }, []);

  const loadSelected = useCallback(async (rulesetId: string, showLoading = true) => {
    if (showLoading) setDetailLoading(true);
    try {
      const [ruleset, items] = await Promise.all([
        ModerationRulesetService.getRuleset(rulesetId),
        ModerationRulesetService.listRules(rulesetId),
      ]);
      setDetail(ruleset);
      setRules(items);
    } finally {
      if (showLoading) setDetailLoading(false);
    }
  }, []);

  const refresh = useCallback(async (preferredId?: string | null, showLoading = false) => {
    const targetId = preferredId ?? selectedId;
    await loadRulesets(targetId);
    if (targetId) await loadSelected(targetId, showLoading);
  }, [loadRulesets, loadSelected, selectedId]);

  useEffect(() => {
    let active = true;
    setListLoading(true);
    setError(null);
    void loadRulesets(linkedRulesetId)
      .catch((caught: unknown) => {
        if (active) setError(caught instanceof Error ? caught.message : "Rulesets could not be loaded.");
      })
      .finally(() => active && setListLoading(false));
    return () => { active = false; };
  }, [linkedRulesetId, loadRulesets]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setRules([]);
      return;
    }
    let active = true;
    setError(null);
    void loadSelected(selectedId)
      .catch((caught: unknown) => {
        if (active) setError(caught instanceof Error ? caught.message : "The ruleset could not be loaded.");
      });
    return () => { active = false; };
  }, [loadSelected, selectedId]);

  useEffect(() => {
    if (!selectedId || !rules.some((rule) => rule.readiness === "preparing")) return;
    const timer = window.setInterval(() => {
      void refresh(selectedId).catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [refresh, rules, selectedId]);

  const visibleRulesets = useMemo(() => {
    const query = rulesetQuery.trim().toLowerCase();
    return [...rulesets]
      .sort((left, right) => {
        if (Boolean(left.archivedAt) !== Boolean(right.archivedAt)) return left.archivedAt ? 1 : -1;
        return left.name.localeCompare(right.name);
      })
      .filter((ruleset) => !query || [ruleset.name, ruleset.description].join(" ").toLowerCase().includes(query));
  }, [rulesetQuery, rulesets]);

  const filteredRules = useMemo(() => {
    const query = ruleQuery.trim().toLowerCase();
    return rules.filter((rule) => {
      if (severityFilter !== "all" && rule.severity !== severityFilter) return false;
      if (readinessFilter !== "all" && rule.readiness !== readinessFilter) return false;
      if (activityFilter === "active" && !rule.active) return false;
      if (activityFilter === "inactive" && rule.active) return false;
      return !query || [rule.name, rule.slug, rule.description].join(" ").toLowerCase().includes(query);
    });
  }, [activityFilter, readinessFilter, ruleQuery, rules, severityFilter]);

  const stats = {
    total: rules.length,
    active: rules.filter((rule) => rule.active).length,
    preparing: rules.filter((rule) => rule.readiness === "preparing").length,
    attention: rules.filter((rule) => rule.readiness === "needs_attention").length,
  };

  const noticeText = useMemo(() => {
    if (!notice) return null;
    if (notice.kind === "text") return notice.text;
    const current = rules.find((rule) => rule.id === notice.ruleId);
    const name = current?.name ?? notice.ruleName;
    if (!current) return `${name} update saved.`;
    if (current.version === notice.version && current.readiness === "preparing") {
      return `${name} version ${notice.version} is preparing.`;
    }
    if (current.version !== notice.version) return `${name} update saved.`;
    if (current.readiness === "needs_attention") {
      return `${name} version ${notice.version} needs attention before it can run.`;
    }
    return `${name} version ${notice.version} is ready.`;
  }, [notice, rules]);

  useEffect(() => {
    if (!notice) return;
    const current = notice.kind === "rule"
      ? rules.find((rule) => rule.id === notice.ruleId)
      : null;
    if (notice.kind === "rule" && current?.readiness === "preparing") return;
    const timer = window.setTimeout(() => setNotice(null), 6000);
    return () => window.clearTimeout(timer);
  }, [notice, rules]);

  async function toggleRule(rule: ModerationRule, active: boolean) {
    if (!detail) return;
    setBusyRuleId(rule.id);
    setError(null);
    try {
      await ModerationRulesetService.setRuleActive(detail.id, rule.id, active);
      await refresh(detail.id);
      setNotice({ kind: "text", text: `${rule.name} is now ${active ? "active" : "inactive"}.` });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The rule state could not be changed.");
    } finally {
      setBusyRuleId(null);
    }
  }

  async function retryRule(rule: ModerationRule) {
    if (!detail) return;
    setBusyRuleId(rule.id);
    setError(null);
    try {
      await ModerationRulesetService.retryRule(detail.id, rule.id);
      await refresh(detail.id);
      setNotice({ kind: "rule", ruleId: rule.id, ruleName: rule.name, version: rule.version });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The rule could not be retried.");
    } finally {
      setBusyRuleId(null);
    }
  }

  async function archiveOrRestore() {
    if (!detail) return;
    const restoring = Boolean(detail.archivedAt);
    if (!restoring) {
      if (detail.assignmentCount > 0) {
        setError("Unassign this ruleset from every catalogue before archiving it.");
        return;
      }
      if (!window.confirm(`Archive “${detail.name}”? You can restore it later.`)) return;
    }
    setRulesetBusy(true);
    setError(null);
    try {
      const updated = restoring
        ? await ModerationRulesetService.restoreRuleset(detail.id)
        : await ModerationRulesetService.archiveRuleset(detail.id);
      await refresh(updated.id);
      setNotice({ kind: "text", text: restoring ? "Ruleset restored." : "Ruleset archived." });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The ruleset state could not be changed.");
    } finally {
      setRulesetBusy(false);
    }
  }

  async function exportRuleset(format: ExportFormat) {
    if (!detail) return;
    setRulesetBusy(true);
    setExportingFormat(format);
    setError(null);
    setNotice(null);
    try {
      const download = await ModerationRulesetService.exportRuleset(detail.id, format);
      saveDownload(download);
      setNotice({ kind: "text", text: exportSuccessMessage("Ruleset", format) });
    } catch (caught) {
      setError(exportErrorMessage("Ruleset", format, caught));
    } finally {
      setRulesetBusy(false);
      setExportingFormat(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
          Rulesets are shared across catalogues. Updating one rule leaves the ruleset in place and
          prepares a new version of that rule only.
        </p>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" /> Import
          </Button>
          <Button onClick={() => setRulesetDialog("create")}>
            <Plus className="h-4 w-4" /> Create ruleset
          </Button>
        </div>
      </div>

      {error ? (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}
      {noticeText ? (
        <div role="status" className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
          {noticeText}
        </div>
      ) : null}

      <Card className="overflow-hidden">
        <div className="grid min-h-[620px] lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside aria-label="Moderation rulesets" className="border-b bg-muted/10 lg:border-b-0 lg:border-r">
            <div className="border-b p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={rulesetQuery}
                  onChange={(event) => setRulesetQuery(event.target.value)}
                  placeholder="Search rulesets"
                  className="pl-9"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto p-2 lg:max-h-[690px]">
              {listLoading ? (
                <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : visibleRulesets.length === 0 ? (
                <div className="px-4 py-12 text-center text-sm text-muted-foreground">
                  {rulesets.length === 0 ? "Create your first ruleset." : "No rulesets match your search."}
                </div>
              ) : (
                <div className="space-y-1">
                  {visibleRulesets.map((ruleset) => (
                    <button
                      key={ruleset.id}
                      type="button"
                      onClick={() => setSelectedId(ruleset.id)}
                      className={cn(
                        "w-full rounded-md px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        selectedId === ruleset.id ? "bg-background shadow-sm ring-1 ring-border" : "hover:bg-muted/60"
                      )}
                    >
                      <span className="flex items-start justify-between gap-2">
                        <span className="line-clamp-2 text-sm font-medium">{ruleset.name}</span>
                        {ruleset.archivedAt ? <Badge variant="outline" className="shrink-0 text-[10px]">Archived</Badge> : null}
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {ruleset.assignmentCount} {ruleset.assignmentCount === 1 ? "catalogue" : "catalogues"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </aside>

          <section aria-live="polite" className="min-w-0">
            {detailLoading ? (
              <div className="flex min-h-[500px] items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading ruleset…
              </div>
            ) : !detail ? (
              <div className="flex min-h-[500px] flex-col items-center justify-center px-6 text-center">
                <Settings2 className="mb-3 h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-medium">No ruleset selected</p>
                <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                  Create a ruleset to start defining catalogue moderation policies.
                </p>
              </div>
            ) : (
              <>
                <header className="border-b p-4 sm:p-6">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="break-words text-xl font-semibold">{detail.name}</h2>
                        {detail.archivedAt ? <Badge variant="outline">Archived</Badge> : null}
                      </div>
                      {detail.description ? (
                        <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">{detail.description}</p>
                      ) : (
                        <p className="mt-2 text-sm text-muted-foreground">No description</p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => setAssignmentsOpen(true)}>
                        <Users className="h-4 w-4" /> Assign catalogues
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            aria-label="Export ruleset"
                            disabled={rulesetBusy}
                          >
                            {exportingFormat
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Download className="h-4 w-4" />}
                            Export
                            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-36"
                          aria-label="Ruleset export format"
                        >
                          <ExportFormatMenuItems
                            disabled={rulesetBusy}
                            onSelect={(format) => void exportRuleset(format)}
                          />
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Ruleset actions" disabled={rulesetBusy}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setRulesetDialog("edit")} disabled={Boolean(detail.archivedAt)}>
                            <Edit3 /> Edit details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onSelect={() => void archiveOrRestore()}
                            disabled={!detail.archivedAt && detail.assignmentCount > 0}
                            className={detail.archivedAt ? undefined : "text-destructive"}
                          >
                            {detail.archivedAt ? <ArchiveRestore /> : <Archive />}
                            {detail.archivedAt ? "Restore ruleset" : "Archive ruleset"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <SummaryItem label="Rules" value={stats.total} />
                    <SummaryItem label="Active" value={stats.active} />
                    <SummaryItem label="Preparing" value={stats.preparing} tone={stats.preparing ? "info" : undefined} />
                    <SummaryItem label="Needs attention" value={stats.attention} tone={stats.attention ? "danger" : undefined} />
                  </dl>
                </header>

                <div className="p-4 sm:p-6">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                      <div>
                        <h3 className="text-base font-semibold">Rules</h3>
                        <p className="text-xs text-muted-foreground">Each saved edit creates a version of that rule only.</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setRuleDialog({ mode: "create" })}
                        disabled={Boolean(detail.archivedAt)}
                      >
                        <Plus className="h-4 w-4" /> Add rule
                      </Button>
                    </div>

                    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_150px_170px_130px]">
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input value={ruleQuery} onChange={(event) => setRuleQuery(event.target.value)} placeholder="Search rules" className="pl-9" />
                      </div>
                      <FilterSelect label="Severity" value={severityFilter} onChange={(value) => setSeverityFilter(value as SeverityFilter)} options={[
                        ["all", "All severities"], ["high", "High"], ["medium", "Medium"], ["low", "Low"],
                      ]} />
                      <FilterSelect label="Readiness" value={readinessFilter} onChange={(value) => setReadinessFilter(value as ReadinessFilter)} options={[
                        ["all", "All readiness"], ["preparing", "Preparing"], ["ready", "Ready"], ["needs_attention", "Needs attention"],
                      ]} />
                      <FilterSelect label="Activity" value={activityFilter} onChange={(value) => setActivityFilter(value as ActivityFilter)} options={[
                        ["all", "All states"], ["active", "Active"], ["inactive", "Inactive"],
                      ]} />
                    </div>

                    {rules.length === 0 ? (
                      <div className="rounded-md border border-dashed px-6 py-14 text-center">
                        <FileClock className="mx-auto h-8 w-8 text-muted-foreground" />
                        <p className="mt-3 text-sm font-medium">No moderation rules yet</p>
                        <p className="mt-1 text-xs text-muted-foreground">Add the first policy this ruleset should enforce.</p>
                        {!detail.archivedAt ? (
                          <Button className="mt-4" size="sm" onClick={() => setRuleDialog({ mode: "create" })}>
                            <Plus className="h-4 w-4" /> Add rule
                          </Button>
                        ) : null}
                      </div>
                    ) : filteredRules.length === 0 ? (
                      <div className="rounded-md border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
                        No rules match these filters.
                      </div>
                    ) : (
                      <RuleList
                        rules={filteredRules}
                        archived={Boolean(detail.archivedAt)}
                        busyRuleId={busyRuleId}
                        onEdit={(rule) => setRuleDialog({ mode: "edit", rule })}
                        onHistory={setHistoryRule}
                        onRetry={(rule) => void retryRule(rule)}
                        onToggle={(rule, active) => void toggleRule(rule, active)}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </Card>

      {rulesetDialog ? (
        <RulesetDetailsDialog
          key={rulesetDialog === "edit" ? detail?.id : "new-ruleset"}
          ruleset={rulesetDialog === "edit" ? detail : null}
          onClose={() => setRulesetDialog(null)}
          onSaved={async (saved) => {
            await refresh(saved.id, true);
            setNotice({
              kind: "text",
              text: rulesetDialog === "edit" ? "Ruleset details saved." : "Ruleset created.",
            });
          }}
        />
      ) : null}
      {importOpen ? (
        <ImportRulesetDialog
          onClose={() => setImportOpen(false)}
          onImported={async (imported) => {
            await refresh(imported.id, true);
            setNotice({
              kind: "text",
              text: "Ruleset imported as an unassigned copy. Its rules start at version 1.",
            });
          }}
        />
      ) : null}
      {detail && assignmentsOpen ? (
        <AssignmentsDialog
          ruleset={detail}
          rulesets={rulesets}
          onClose={() => setAssignmentsOpen(false)}
          onChanged={() => refresh(detail.id)}
        />
      ) : null}
      {detail && ruleDialog ? (
        <RuleEditorDialog
          key={ruleDialog.mode === "edit" ? ruleDialog.rule.id : "new-rule"}
          rulesetId={detail.id}
          rule={ruleDialog.mode === "edit" ? ruleDialog.rule : null}
          onClose={() => setRuleDialog(null)}
          onSaved={async (saved) => {
            await refresh(detail.id);
            if (ruleDialog.mode === "edit" && saved.version === ruleDialog.rule.version) {
              setNotice({ kind: "text", text: `${saved.name} is already up to date.` });
            } else {
              setNotice({
                kind: "rule",
                ruleId: saved.id,
                ruleName: saved.name,
                version: saved.version,
              });
            }
          }}
        />
      ) : null}
      {detail && historyRule ? (
        <VersionHistoryDialog rulesetId={detail.id} rule={historyRule} onClose={() => setHistoryRule(null)} />
      ) : null}
    </div>
  );
}

function SummaryItem({ label, value, tone }: { label: string; value: number; tone?: "info" | "danger" }) {
  return (
    <div className={cn(
      "rounded-md border bg-muted/20 p-3",
      tone === "info" && "border-sky-200 bg-sky-50",
      tone === "danger" && "border-red-200 bg-red-50"
    )}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <label>
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>{optionLabel}</option>
        ))}
      </select>
    </label>
  );
}

/**
 * The customer's own rejected examples, shown where the rule is read rather
 * than only inside the editor. The caption is their stated reason, so it is
 * the alt text as well as the enlarged view's description.
 */
function ReferenceStrip({ references }: { references: ModerationReferenceImage[] }) {
  const [enlarged, setEnlarged] = useState<ModerationReferenceImage | null>(null);
  if (references.length === 0) return null;

  const flagged = references.filter((reference) => reference.role === "flagged").length;
  const acceptable = references.length - flagged;

  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Examples</span>
        {flagged > 0 ? <span>{flagged} flagged</span> : null}
        {acceptable > 0 ? <span>{acceptable} acceptable</span> : null}
      </div>
      <ul className="mt-2 flex flex-wrap gap-2">
        {references.map((reference) => (
          <li key={reference.assetId}>
            <button
              type="button"
              onClick={() => setEnlarged(reference)}
              title={reference.caption ?? undefined}
              aria-label={reference.caption ?? `Enlarge ${reference.role} example`}
              className={cn(
                "block h-14 w-14 overflow-hidden rounded-md border bg-muted transition-shadow",
                "hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                reference.role === "flagged"
                  ? "border-red-200 dark:border-red-900"
                  : "border-emerald-200 dark:border-emerald-900",
              )}
            >
              <img
                src={reference.assetUrl}
                alt={reference.caption ?? `${reference.role} example`}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </button>
          </li>
        ))}
      </ul>

      <Dialog open={enlarged !== null} onOpenChange={(open) => !open && setEnlarged(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-base">
              {enlarged?.role === "acceptable" ? "Acceptable example" : "Flagged example"}
            </DialogTitle>
            {enlarged?.caption ? (
              <DialogDescription>{enlarged.caption}</DialogDescription>
            ) : null}
          </DialogHeader>
          {enlarged ? (
            <img
              src={enlarged.assetUrl}
              alt={enlarged.caption ?? `${enlarged.role} example`}
              className="max-h-[70vh] w-full rounded-md object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RuleList({
  rules,
  archived,
  busyRuleId,
  onEdit,
  onHistory,
  onRetry,
  onToggle,
}: {
  rules: ModerationRule[];
  archived: boolean;
  busyRuleId: string | null;
  onEdit: (rule: ModerationRule) => void;
  onHistory: (rule: ModerationRule) => void;
  onRetry: (rule: ModerationRule) => void;
  onToggle: (rule: ModerationRule, active: boolean) => void;
}) {
  return (
    <>
      <div className="space-y-3 2xl:hidden">
        {rules.map((rule) => (
          <article key={rule.id} className="rounded-md border p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">{rule.name}</p>
                <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{rule.slug}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {busyRuleId === rule.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                <Switch
                  checked={rule.active}
                  onCheckedChange={(checked) => onToggle(rule, checked)}
                  disabled={archived || busyRuleId !== null}
                  aria-label={`${rule.active ? "Deactivate" : "Activate"} ${rule.name}`}
                />
                <RuleActions
                  rule={rule}
                  archived={archived}
                  busy={busyRuleId === rule.id}
                  onEdit={onEdit}
                  onHistory={onHistory}
                  onRetry={onRetry}
                  onToggle={onToggle}
                />
              </div>
            </div>
            <p className="mt-3 line-clamp-3 text-xs leading-5 text-muted-foreground">{rule.description}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge variant="outline" className={cn("capitalize", severityClass(rule.severity))}>{rule.severity}</Badge>
              <Badge variant="outline" className={readinessClass(rule.readiness)}>{readinessLabels[rule.readiness]}</Badge>
              <Badge variant="outline">Latest v{rule.version}</Badge>
              <Badge variant="outline">Effective {rule.effectiveVersion ? `v${rule.effectiveVersion}` : "—"}</Badge>
              <Badge variant="outline">{rule.active ? "Active" : "Inactive"}</Badge>
              {rule.readiness === "needs_attention" ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  disabled={archived || busyRuleId !== null}
                  onClick={() => onRetry(rule)}
                >
                  <RefreshCw className="h-3 w-3" /> Retry
                </Button>
              ) : null}
            </div>
            {rule.readiness === "needs_attention" && rule.statusMessage ? (
              <p className="mt-2 text-xs text-red-700 dark:text-red-300">{rule.statusMessage}</p>
            ) : null}
            <ReferenceStrip references={rule.referenceImages} />
            <dl className="mt-4 grid grid-cols-2 gap-3 border-t pt-3 text-xs sm:grid-cols-3">
              <div><dt className="text-muted-foreground">Runs</dt><dd className="mt-0.5">{runAtLabel(rule)}</dd></div>
              <div><dt className="text-muted-foreground">Images</dt><dd className="mt-0.5">{imageScopeLabel(rule)}</dd></div>
              <div><dt className="text-muted-foreground">Violation action</dt><dd className="mt-0.5">{rule.autoUnpublish ? "Auto-unpublish" : "Record for review"}</dd></div>
            </dl>
          </article>
        ))}
      </div>

      <div className="hidden overflow-x-auto rounded-md border 2xl:block">
        <table className="w-full min-w-[1040px] text-left text-sm">
          <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Rule</th>
              <th className="px-4 py-3 font-medium">Version</th>
              <th className="px-4 py-3 font-medium">Severity</th>
              <th className="px-4 py-3 font-medium">Runs</th>
              <th className="px-4 py-3 font-medium">Images</th>
              <th className="px-4 py-3 font-medium">Examples</th>
              <th className="px-4 py-3 font-medium">Readiness</th>
              <th className="px-4 py-3 font-medium">Active</th>
              <th className="w-12 px-4 py-3"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id} className="border-b last:border-0 hover:bg-muted/20">
                <td className="max-w-sm px-4 py-3">
                  <button type="button" onClick={() => onEdit(rule)} className="text-left focus-visible:outline-none focus-visible:underline" disabled={archived || rule.readiness === "preparing"}>
                    <span className="block font-medium">{rule.name}</span>
                    <span className="mt-0.5 block font-mono text-xs text-muted-foreground">{rule.slug}</span>
                    <span className="mt-1 block line-clamp-1 text-xs text-muted-foreground">{rule.description}</span>
                  </button>
                </td>
                <td className="px-4 py-3 text-xs">
                  <p>Latest v{rule.version}</p>
                  <p className="text-muted-foreground">Effective {rule.effectiveVersion ? `v${rule.effectiveVersion}` : "—"}</p>
                </td>
                <td className="px-4 py-3"><Badge variant="outline" className={cn("capitalize", severityClass(rule.severity))}>{rule.severity}</Badge></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{runAtLabel(rule)}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{imageScopeLabel(rule)}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {rule.referenceImages.length === 0 ? "—" : rule.referenceImages.length}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col items-start gap-1.5">
                    <Badge variant="outline" className={readinessClass(rule.readiness)}>{readinessLabels[rule.readiness]}</Badge>
                    {rule.readiness === "needs_attention" && rule.statusMessage ? (
                      <span className="max-w-48 text-xs text-red-700 dark:text-red-300">{rule.statusMessage}</span>
                    ) : null}
                    {rule.readiness === "needs_attention" ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-xs"
                        disabled={archived || busyRuleId !== null}
                        onClick={() => onRetry(rule)}
                      >
                        <RefreshCw className="h-3 w-3" /> Retry
                      </Button>
                    ) : null}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {busyRuleId === rule.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    <Switch
                      checked={rule.active}
                      onCheckedChange={(checked) => onToggle(rule, checked)}
                      disabled={archived || busyRuleId !== null}
                      aria-label={`${rule.active ? "Deactivate" : "Activate"} ${rule.name}`}
                    />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <RuleActions
                    rule={rule}
                    archived={archived}
                    busy={busyRuleId === rule.id}
                    onEdit={onEdit}
                    onHistory={onHistory}
                    onRetry={onRetry}
                    onToggle={onToggle}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RuleActions({
  rule,
  archived,
  busy,
  onEdit,
  onHistory,
  onRetry,
  onToggle,
}: {
  rule: ModerationRule;
  archived: boolean;
  busy: boolean;
  onEdit: (rule: ModerationRule) => void;
  onHistory: (rule: ModerationRule) => void;
  onRetry: (rule: ModerationRule) => void;
  onToggle: (rule: ModerationRule, active: boolean) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${rule.name}`} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onEdit(rule)} disabled={archived || rule.readiness === "preparing"}>
          <Edit3 /> Edit rule
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onHistory(rule)}>
          <History /> Version history
        </DropdownMenuItem>
        {rule.readiness === "needs_attention" ? (
          <DropdownMenuItem onSelect={() => onRetry(rule)} disabled={archived}>
            <RefreshCw /> Retry preparation
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onToggle(rule, !rule.active)} disabled={archived}>
          {rule.active ? "Deactivate rule" : "Activate rule"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
